import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Image, Download, Sparkles, Loader2, RefreshCw, Wand2, BookOpen, Tag, Type, Palette, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const API = process.env.REACT_APP_BACKEND_URL;

// Banner resolutions
const RESOLUTIONS = [
  { id: 'instagram-post', label: 'Instagram Post', width: 1080, height: 1080, ratio: '1:1' },
  { id: 'instagram-story', label: 'Instagram Story', width: 1080, height: 1920, ratio: '9:16' },
  { id: 'facebook-post', label: 'Facebook Post', width: 1200, height: 630, ratio: '1.91:1' },
  { id: 'twitter', label: 'Twitter/X', width: 1200, height: 675, ratio: '16:9' },
  { id: 'youtube-thumb', label: 'YouTube Thumbnail', width: 1280, height: 720, ratio: '16:9' },
  { id: 'web-banner', label: 'Web Banner', width: 1920, height: 600, ratio: '3.2:1' },
];

// Color themes
const THEMES = [
  { id: 'viubux', label: 'Viubux', primary: '#8B5CF6', secondary: '#EC4899', text: '#FFFFFF', bg: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' },
  { id: 'elegant', label: 'Elegante', primary: '#D4AF37', secondary: '#C0C0C0', text: '#FFFFFF', bg: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' },
  { id: 'modern', label: 'Moderno', primary: '#00D4FF', secondary: '#7C3AED', text: '#FFFFFF', bg: 'linear-gradient(135deg, #0f0f23 0%, #1e1e3f 100%)' },
  { id: 'warm', label: 'Cálido', primary: '#F97316', secondary: '#EF4444', text: '#FFFFFF', bg: 'linear-gradient(135deg, #7c2d12 0%, #b91c1c 100%)' },
  { id: 'nature', label: 'Naturaleza', primary: '#22C55E', secondary: '#14B8A6', text: '#FFFFFF', bg: 'linear-gradient(135deg, #14532d 0%, #0f766e 100%)' },
];

const VIUBUX_LOGO = 'https://connect.viubux.com/viubux-logo-white.png';

const BannerGenerator = ({ user, token }) => {
  const [works, setWorks] = useState([]);
  const [selectedWork, setSelectedWork] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedCopy, setGeneratedCopy] = useState('');
  const [customCopy, setCustomCopy] = useState('');
  const [selectedResolution, setSelectedResolution] = useState(RESOLUTIONS[0]);
  const [selectedTheme, setSelectedTheme] = useState(THEMES[0]);
  const [keywords, setKeywords] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [useAIImage, setUseAIImage] = useState(false);
  const bannerRef = useRef(null);

  // Fetch user's works
  useEffect(() => {
    const fetchWorks = async () => {
      try {
        const response = await axios.get(`${API}/api/user/works`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWorks(response.data || []);
      } catch (error) {
        console.error('Error fetching works:', error);
      }
    };
    fetchWorks();
  }, [token]);

  // Generate catchy copy based on synopsis
  const generateCopy = async () => {
    if (!selectedWork?.synopsis && !customCopy) {
      toast.error('Selecciona una obra con sinopsis o escribe tu propio copy');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/banner/generate-copy`,
        {
          synopsis: selectedWork?.synopsis || '',
          keywords: keywords,
          work_title: selectedWork?.title || '',
          custom_text: customCopy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedCopy(response.data.copy);
      toast.success('Copy generado exitosamente');
    } catch (error) {
      console.error('Error generating copy:', error);
      toast.error('Error al generar el copy');
    } finally {
      setGenerating(false);
    }
  };

  // Generate AI image based on work
  const generateAIImage = async () => {
    if (!selectedWork) {
      toast.error('Selecciona una obra primero');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/banner/generate-image`,
        {
          work_title: selectedWork.title,
          synopsis: selectedWork.synopsis || '',
          keywords: keywords,
          cover_url: selectedWork.cover_url
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedImageUrl(response.data.image_url);
      setUseAIImage(true);
      toast.success('Imagen generada exitosamente');
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Error al generar la imagen');
    } finally {
      setGenerating(false);
    }
  };

  // Download banner
  const downloadBanner = async () => {
    if (!bannerRef.current) return;
    
    setLoading(true);
    try {
      const canvas = await html2canvas(bannerRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: selectedResolution.width / 2,
        height: selectedResolution.height / 2,
      });
      
      const link = document.createElement('a');
      link.download = `viubux-banner-${selectedResolution.id}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Banner descargado');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar');
    } finally {
      setLoading(false);
    }
  };

  const finalCopy = generatedCopy || customCopy || (selectedWork ? `Descubre "${selectedWork.title}"` : 'Tu próxima historia favorita te espera');

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="bg-gradient-to-r from-pink-600 to-violet-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Image className="w-8 h-8" />
          <h3 className="text-xl font-bold">Generador de Banners</h3>
        </div>
        <p className="text-white/80 text-sm">
          Crea banners atractivos para promocionar tus obras. Genera copies persuasivos automáticamente 
          basados en la sinopsis de tu libro y descárgalos en múltiples resoluciones para todas las redes sociales.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="space-y-4">
          {/* Select Work */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <BookOpen className="w-4 h-4" />
              Seleccionar Obra
            </label>
            <select
              value={selectedWork?.id || ''}
              onChange={(e) => {
                const work = works.find(w => w.id === e.target.value);
                setSelectedWork(work || null);
                setGeneratedCopy('');
                setGeneratedImageUrl(null);
              }}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">-- Selecciona una obra --</option>
              {works.map(work => (
                <option key={work.id} value={work.id}>{work.title}</option>
              ))}
            </select>
            
            {selectedWork && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex gap-3">
                  {selectedWork.cover_url && (
                    <img 
                      src={selectedWork.cover_url} 
                      alt={selectedWork.title}
                      className="w-16 h-24 object-cover rounded"
                    />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{selectedWork.title}</p>
                    <p className="text-xs text-gray-500 line-clamp-3">{selectedWork.synopsis || 'Sin sinopsis'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Keywords */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4" />
              Palabras Clave (opcional)
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="ej: romance, aventura, misterio"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Custom Copy */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Type className="w-4 h-4" />
              Copy del Banner
            </label>
            <textarea
              value={generatedCopy || customCopy}
              onChange={(e) => {
                setCustomCopy(e.target.value);
                setGeneratedCopy('');
              }}
              placeholder="Escribe tu copy o genera uno automáticamente..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
            />
            <Button
              onClick={generateCopy}
              disabled={generating || (!selectedWork?.synopsis && !customCopy)}
              className="mt-2 w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" /> Auto-generar Copy</>
              )}
            </Button>
          </div>

          {/* Theme Selection */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Palette className="w-4 h-4" />
              Tema de Color
            </label>
            <div className="grid grid-cols-5 gap-2">
              {THEMES.map(theme => (
                <button
                  key={theme.id}
                  onClick={() => setSelectedTheme(theme)}
                  className={`relative p-3 rounded-lg transition-all ${
                    selectedTheme.id === theme.id 
                      ? 'ring-2 ring-violet-500 ring-offset-2' 
                      : 'hover:scale-105'
                  }`}
                  style={{ background: theme.bg }}
                >
                  <div className="flex gap-1 justify-center">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.primary }} />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.secondary }} />
                  </div>
                  <span className="text-xs text-white/80 mt-1 block text-center">{theme.label}</span>
                  {selectedTheme.id === theme.id && (
                    <Check className="absolute -top-1 -right-1 w-4 h-4 bg-violet-500 rounded-full text-white p-0.5" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Resolution Selection */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Image className="w-4 h-4" />
              Resolución
            </label>
            <div className="grid grid-cols-2 gap-2">
              {RESOLUTIONS.map(res => (
                <button
                  key={res.id}
                  onClick={() => setSelectedResolution(res)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedResolution.id === res.id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-gray-900 text-sm">{res.label}</p>
                  <p className="text-xs text-gray-500">{res.width} x {res.height}</p>
                </button>
              ))}
            </div>
          </div>

          {/* AI Image Generation */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Sparkles className="w-4 h-4" />
              Imagen de Fondo
            </label>
            <div className="flex gap-2">
              <Button
                variant={!useAIImage ? 'default' : 'outline'}
                onClick={() => setUseAIImage(false)}
                className="flex-1"
              >
                Usar Portada
              </Button>
              <Button
                variant={useAIImage ? 'default' : 'outline'}
                onClick={generateAIImage}
                disabled={generating || !selectedWork}
                className="flex-1"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Generar con IA
              </Button>
            </div>
          </div>

          {/* Download Button */}
          <Button
            onClick={downloadBanner}
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Descargando...</>
            ) : (
              <><Download className="w-5 h-5 mr-2" /> Descargar Banner ({selectedResolution.width}x{selectedResolution.height})</>
            )}
          </Button>
        </div>

        {/* Preview Panel */}
        <div className="bg-gray-100 rounded-xl p-4">
          <h4 className="font-medium text-gray-700 mb-4 flex items-center gap-2">
            <Image className="w-4 h-4" />
            Vista Previa
          </h4>
          
          <div className="flex justify-center items-center overflow-hidden rounded-xl shadow-lg">
            {/* Banner Preview */}
            <div
              ref={bannerRef}
              className="relative overflow-hidden"
              style={{
                width: Math.min(selectedResolution.width / 2, 400),
                height: Math.min(selectedResolution.height / 2, 400),
                background: selectedTheme.bg,
              }}
            >
              {/* Background Image */}
              {(selectedWork?.cover_url || generatedImageUrl) && (
                <div 
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage: `url(${useAIImage && generatedImageUrl ? generatedImageUrl : selectedWork?.cover_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(8px)',
                  }}
                />
              )}
              
              {/* Overlay Gradient */}
              <div 
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${selectedTheme.primary}40 0%, ${selectedTheme.secondary}40 100%)`,
                }}
              />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-6">
                {/* Viubux Logo */}
                <div className="flex justify-end">
                  <img 
                    src="/viubux-logo-white.png" 
                    alt="Viubux" 
                    className="h-6 opacity-90"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  {/* Book Cover */}
                  {selectedWork?.cover_url && !useAIImage && (
                    <img 
                      src={selectedWork.cover_url}
                      alt={selectedWork.title}
                      className="w-20 h-28 object-cover rounded-lg shadow-2xl mb-4 border-2 border-white/20"
                    />
                  )}

                  {/* Title */}
                  {selectedWork?.title && (
                    <h2 
                      className="font-bold mb-2 text-shadow"
                      style={{ 
                        color: selectedTheme.text,
                        fontSize: selectedResolution.width > 1000 ? '1.5rem' : '1rem',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      }}
                    >
                      {selectedWork.title}
                    </h2>
                  )}

                  {/* Generated Copy */}
                  <p 
                    className="font-medium leading-snug max-w-[80%]"
                    style={{ 
                      color: selectedTheme.text,
                      fontSize: selectedResolution.width > 1000 ? '0.9rem' : '0.7rem',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {finalCopy}
                  </p>
                </div>

                {/* CTA */}
                <div className="flex justify-center">
                  <div 
                    className="px-4 py-2 rounded-full font-bold text-xs"
                    style={{
                      backgroundColor: selectedTheme.primary,
                      color: selectedTheme.text,
                    }}
                  >
                    Escucha ahora en Viubux
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4">
            Resolución: {selectedResolution.width} x {selectedResolution.height} px
          </p>
        </div>
      </div>
    </div>
  );
};

export default BannerGenerator;
